// Utility functions for fixing malformed markdown tables and parsing custom table syntax

interface TableRow {
  cells: string[];
  isHeader: boolean;
  isSeparator: boolean;
}

// Custom table format interfaces
export interface TableConfig {
  title?: string;
  headers: string[];
  rows: string[][];
  alignment?: ('left' | 'center' | 'right')[];
}

export interface ParsedTable {
  placeholder: string;
  config: TableConfig;
}

// Fix malformed markdown tables in content
export function fixMalformedTables(content: string): string {
  // First, replace HTML entities
  let fixed = content.replace(/&#124;/g, '|');
  
  // Find all potential tables (lines with pipes)
  const lines = fixed.split('\n');
  const processedLines: string[] = [];
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Check if this line starts a table
    if (line.includes('|') && !line.trim().startsWith('*') && !line.trim().startsWith('-')) {
      const tableLines: string[] = [];
      let j = i;
      
      // Collect all consecutive lines that look like table rows
      while (j < lines.length && (lines[j].includes('|') || lines[j].match(/^\s*\|\s*$/))) {
        tableLines.push(lines[j]);
        j++;
      }
      
      // Process the table if we found one
      if (tableLines.length >= 2) {
        const fixedTable = fixTableStructure(tableLines);
        processedLines.push(...fixedTable);
        i = j;
        continue;
      }
    }
    
    processedLines.push(line);
    i++;
  }
  
  return processedLines.join('\n');
}

// Fix the structure of a single table
function fixTableStructure(tableLines: string[]): string[] {
  const rows: TableRow[] = [];
  
  // Parse each line into cells
  for (const line of tableLines) {
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed || trimmed === '|') continue;
    
    // Check if it's a separator line
    const isSeparator = /^\|?\s*(:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/.test(trimmed);
    
    // Extract cells
    const cells = trimmed
      .split('|')
      .map(cell => cell.trim())
      .filter((cell, index, arr) => {
        // Keep cells that are not at the edges unless they have content
        return index > 0 && index < arr.length - 1 || cell !== '';
      });
    
    if (cells.length > 0) {
      rows.push({
        cells,
        isHeader: rows.length === 0 && !isSeparator,
        isSeparator
      });
    }
  }
  
  if (rows.length === 0) return tableLines;
  
  // Find the maximum number of columns (excluding separator rows)
  const dataCells = rows.filter(r => !r.isSeparator);
  if (dataCells.length === 0) return tableLines;
  
  // Analyze column usage to determine actual table structure
  const columnUsage = analyzeColumnUsage(dataCells);
  const actualColumns = determineActualColumns(columnUsage);
  
  // Rebuild the table with correct structure
  const fixedRows: string[] = [];
  
  for (let idx = 0; idx < rows.length; idx++) {
    const row = rows[idx];
    
    if (row.isSeparator) {
      // Create separator with correct number of columns
      const separator = '|' + actualColumns.map(() => '---').join('|') + '|';
      fixedRows.push(separator);
    } else {
      // Fix data rows
      const fixedCells = fixRowCells(row.cells, actualColumns, columnUsage);
      const fixedRow = '| ' + fixedCells.join(' | ') + ' |';
      fixedRows.push(fixedRow);
    }
  }
  
  // Ensure there's a separator after the header
  if (fixedRows.length >= 2 && !rows[1]?.isSeparator) {
    const separator = '|' + actualColumns.map(() => '---').join('|') + '|';
    fixedRows.splice(1, 0, separator);
  }
  
  return fixedRows;
}

// Analyze which columns actually contain data
function analyzeColumnUsage(rows: TableRow[]): boolean[][] {
  const maxCols = Math.max(...rows.map(r => r.cells.length));
  const usage: boolean[][] = [];
  
  for (const row of rows) {
    const rowUsage: boolean[] = [];
    for (let i = 0; i < maxCols; i++) {
      rowUsage.push(i < row.cells.length && row.cells[i] !== '');
    }
    usage.push(rowUsage);
  }
  
  return usage;
}

// Determine which columns should actually exist in the table
function determineActualColumns(usage: boolean[][]): number[] {
  if (usage.length === 0) return [];
  
  const maxCols = usage[0].length;
  const actualCols: number[] = [];
  
  // A column is "actual" if it has content in any row
  for (let col = 0; col < maxCols; col++) {
    const hasContent = usage.some(row => row[col]);
    if (hasContent) {
      actualCols.push(col);
    }
  }
  
  // If we end up with inconsistent column usage, try a different approach
  // Look for patterns like the statistical table where some rows span multiple columns
  if (actualCols.length === 0 || seemsLikeComplexTable(usage)) {
    return smartColumnDetection(usage);
  }
  
  return actualCols;
}

// Detect complex tables with merged cells or spanning content
function seemsLikeComplexTable(usage: boolean[][]): boolean {
  // Check if different rows have very different column usage patterns
  const columnCounts = usage.map(row => row.filter(Boolean).length);
  const uniqueCounts = new Set(columnCounts);
  
  // If rows have very different numbers of filled columns, it's likely complex
  return uniqueCounts.size > 2 && Math.max(...columnCounts) - Math.min(...columnCounts) > 2;
}

// Smart detection for complex statistical tables
function smartColumnDetection(usage: boolean[][]): number[] {
  // For complex tables, we'll try to detect the intended structure
  // by looking at the most common column count
  const columnCounts = usage.map(row => {
    let count = 0;
    for (let i = row.length - 1; i >= 0; i--) {
      if (row[i]) {
        count = i + 1;
        break;
      }
    }
    return count;
  });
  
  // Use the maximum column count as the target
  const maxColumns = Math.max(...columnCounts);
  
  // Return all columns up to the maximum
  return Array.from({ length: maxColumns }, (_, i) => i);
}

// Fix cells in a row to match the actual column structure
function fixRowCells(cells: string[], actualColumns: number[], usage: boolean[][]): string[] {
  const fixedCells: string[] = [];
  
  // For statistical tables with complex structure, try to preserve the content
  if (cells.some(cell => cell.includes('=') || cell.includes(':'))) {
    // This looks like a statistical output row
    return preserveStatisticalRow(cells, actualColumns.length);
  }
  
  // Map cells to actual columns
  let cellIndex = 0;
  for (let i = 0; i < actualColumns.length; i++) {
    if (cellIndex < cells.length) {
      fixedCells.push(cells[cellIndex] || '');
      cellIndex++;
    } else {
      fixedCells.push('');
    }
  }
  
  return fixedCells;
}

// Special handling for statistical output rows
function preserveStatisticalRow(cells: string[], targetColumns: number): string[] {
  // If we have fewer cells than columns, pad with empty cells
  if (cells.length < targetColumns) {
    const padded = [...cells];
    while (padded.length < targetColumns) {
      padded.push('');
    }
    return padded;
  }
  
  // If we have more cells than columns, try to merge intelligently
  if (cells.length > targetColumns) {
    // For statistical tables, keep the most important cells
    // This is a heuristic - you might need to adjust based on your data
    return cells.slice(0, targetColumns);
  }
  
  return cells;
}

// Additional function to detect and fix specific statistical table patterns
export function preprocessStatisticalTables(content: string): string {
  // Fix specific patterns like "| diff | | | | | 2.082221   7.594243 |"
  // where values are incorrectly placed in the last column
  
  let processed = content.replace(/\|\s*([^|]+?)\s*\|\s*\|\s*\|\s*\|\s*\|\s*([0-9.-]+)\s+([0-9.-]+)\s*\|/g, 
    (match, label, val1, val2) => {
      // Redistribute the values properly
      return `| ${label} | ${val1} | ${val2} | | | |`;
    });
    
  // Also fix tables where rows have inconsistent column counts
  // by detecting table blocks and normalizing them
  const lines = processed.split('\n');
  const fixedLines: string[] = [];
  let inTable = false;
  let tableLines: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const hasTableDelimiter = line.includes('|');
    const isSeparatorLine = /^\s*\|?\s*(:?-+:?\s*\|)+/.test(line);
    
    if (hasTableDelimiter && !inTable) {
      // Start of a table
      inTable = true;
      tableLines = [line];
    } else if (inTable && (hasTableDelimiter || isSeparatorLine)) {
      // Continue table
      tableLines.push(line);
    } else if (inTable && !hasTableDelimiter) {
      // End of table - process it
      const fixedTable = normalizeTableColumns(tableLines);
      fixedLines.push(...fixedTable);
      fixedLines.push(line); // Add the non-table line
      inTable = false;
      tableLines = [];
    } else {
      // Regular line
      fixedLines.push(line);
    }
  }
  
  // Don't forget the last table if we ended while in one
  if (inTable && tableLines.length > 0) {
    const fixedTable = normalizeTableColumns(tableLines);
    fixedLines.push(...fixedTable);
  }
  
  return fixedLines.join('\n');
}

// Normalize tables to have consistent column counts
function normalizeTableColumns(tableLines: string[]): string[] {
  if (tableLines.length === 0) return tableLines;
  
  // Find the maximum number of columns
  let maxColumns = 0;
  const parsedRows: string[][] = [];
  
  for (const line of tableLines) {
    if (line.trim()) {
      const cells = line.split('|').map(c => c.trim());
      // Remove empty first/last elements from split
      if (cells[0] === '') cells.shift();
      if (cells[cells.length - 1] === '') cells.pop();
      
      parsedRows.push(cells);
      maxColumns = Math.max(maxColumns, cells.length);
    }
  }
  
  // Rebuild with consistent columns
  const normalized: string[] = [];
  for (let i = 0; i < parsedRows.length; i++) {
    const cells = parsedRows[i];
    
    // Check if this is a separator line
    if (cells.every(cell => /^:?-+:?$/.test(cell))) {
      // It's a separator - make sure it has the right number of columns
      const separator = '|' + Array(maxColumns).fill('---').join('|') + '|';
      normalized.push(separator);
    } else {
      // Regular row - pad with empty cells if needed
      while (cells.length < maxColumns) {
        cells.push('');
      }
      normalized.push('| ' + cells.join(' | ') + ' |');
    }
  }
  
  return normalized;
}

// Extract table data for custom rendering if markdown parsing fails
export function extractTableData(content: string): { tables: string[][], positions: number[] } {
  const tables: string[][] = [];
  const positions: number[] = [];
  const lines = content.split('\n');
  
  let currentTable: string[] = [];
  let tableStart = -1;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.includes('|') && !line.trim().startsWith('*')) {
      if (tableStart === -1) {
        tableStart = i;
      }
      currentTable.push(line);
    } else if (currentTable.length > 0) {
      // End of table
      tables.push([...currentTable]);
      positions.push(tableStart);
      currentTable = [];
      tableStart = -1;
    }
  }
  
  // Don't forget the last table
  if (currentTable.length > 0) {
    tables.push(currentTable);
    positions.push(tableStart);
  }
  
  return { tables, positions };
}

// Parse row cells handling quoted strings with commas and line breaks
const parseRowCells = (rowContent: string): string[] => {
  const cells: string[] = [];
  let currentCell = '';
  let inQuotes = false;
  let quoteChar = '';
  let i = 0;
  
  while (i < rowContent.length) {
    const char = rowContent[i];
    
    if (!inQuotes) {
      // Not inside quotes
      if (char === '"' || char === "'") {
        // Start of quoted string
        inQuotes = true;
        quoteChar = char;
      } else if (char === ',') {
        const prevNonSpace = (() => {
          let j = i - 1;
          while (j >= 0 && rowContent[j] === ' ') j--;
          return j >= 0 ? rowContent[j] : '';
        })();
        const nextNonSpace = (() => {
          let j = i + 1;
          while (j < rowContent.length && rowContent[j] === ' ') j++;
          return j < rowContent.length ? rowContent[j] : '';
        })();
        const partOfNumber = /[\d)]/.test(prevNonSpace) && /[\d(]/.test(nextNonSpace);
        if (partOfNumber) {
          currentCell += char;
        } else {
          cells.push(currentCell.trim());
          currentCell = '';
        }
      } else if (char !== ' ' || currentCell.length > 0) {
        // Add character (skip leading spaces)
        currentCell += char;
      }
    } else {
      // Inside quotes
      if (char === '\\' && i + 1 < rowContent.length) {
        const nextChar = rowContent[i + 1];
        if (nextChar === quoteChar) {
          currentCell += nextChar;
          i += 2;
          continue;
        }
      }
      if (char === quoteChar) {
        // Check if this is an escaped quote
        if (i + 1 < rowContent.length && rowContent[i + 1] === quoteChar) {
          // Escaped quote - add one quote and skip the next
          currentCell += char;
          i++; // Skip the next quote
        } else {
          // End of quoted string
          inQuotes = false;
          quoteChar = '';
        }
      } else {
        // Regular character inside quotes
        currentCell += char;
      }
    }
    
    i++;
  }
  
  // Add the last cell
  if (currentCell.trim().length > 0 || cells.length > 0) {
    cells.push(currentCell.trim());
  }
  
  return cells;
};

// Improved table configuration parser
const parseTableConfig = (configText: string): TableConfig | null => {
  try {
    const lines = configText.trim().split('\n').map(line => line.trim());
    const config: Partial<TableConfig> = {};
    
    let currentKey = '';
    let inArraySection = false;
    let arrayContent: string[] = [];
    let rowsContent: string[][] = [];
    let multilineRow = false;
    let currentRowContent = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip empty lines
      if (!line) continue;
      
      // Check for key-value pairs (key: value)
      if (line.includes(':') && !inArraySection) {
        const colonIndex = line.indexOf(':');
        const key = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        
        if (key === 'title') {
          // Remove quotes from title
          config.title = value.replace(/^["']|["']$/g, '');
        } else if (key === 'headers' || key === 'alignment') {
          currentKey = key;
          // Check if value starts with [ (inline array)
          if (value.startsWith('[')) {
            // Parse inline array
            const arrayStr = value.endsWith(']') ? value : value + ']'; // Handle incomplete lines
            const arrayMatch = arrayStr.match(/\[(.*)\]/);
            if (arrayMatch) {
              const items = parseRowCells(arrayMatch[1]);
              if (key === 'headers') {
                config.headers = items;
              } else {
                config.alignment = items as ('left' | 'center' | 'right')[];
              }
            }
            currentKey = '';
          } else if (value === '[') {
            // Start of multiline array
            inArraySection = true;
            arrayContent = [];
          }
        } else if (key === 'rows') {
          currentKey = key;
          if (value === '[') {
            // Start of multiline rows array
            inArraySection = true;
            rowsContent = [];
          }
        }
        continue;
      }
      
      // Handle array content
      if (inArraySection) {
        if (line === ']' || line === '],') {
          // End of array - process any pending multiline row
          if (multilineRow && currentRowContent && currentKey === 'rows') {
            const cells = parseRowCells(currentRowContent);
            rowsContent.push(cells);
            multilineRow = false;
            currentRowContent = '';
          }
          
          inArraySection = false;
          
          if (currentKey === 'headers') {
            config.headers = arrayContent;
          } else if (currentKey === 'alignment') {
            config.alignment = arrayContent as ('left' | 'center' | 'right')[];
          } else if (currentKey === 'rows') {
            config.rows = rowsContent;
          }
          
          currentKey = '';
          arrayContent = [];
          rowsContent = [];
        } else if (line.startsWith('[') && (line.endsWith(']') || line.endsWith('],'))) {
          // Complete single-line row array (with or without trailing comma)
          if (currentKey === 'rows') {
            // Remove brackets and optional comma
            const rowContent = line.endsWith('],') ? line.slice(1, -2) : line.slice(1, -1);
            const cells = parseRowCells(rowContent);
            rowsContent.push(cells);
          }
        } else if (line.startsWith('[') && !line.endsWith(']')) {
          // Start of multiline row
          if (currentKey === 'rows') {
            multilineRow = true;
            currentRowContent = line.slice(1); // Remove opening bracket
          }
        } else if (multilineRow && line.endsWith(']')) {
          // End of multiline row
          if (currentKey === 'rows') {
            currentRowContent += line.slice(0, -1); // Remove closing bracket
            const cells = parseRowCells(currentRowContent);
            rowsContent.push(cells);
            multilineRow = false;
            currentRowContent = '';
          }
        } else if (multilineRow) {
          // Continue multiline row
          currentRowContent += line;
        } else if (line.startsWith('$') && line.endsWith('$')) {
          // Handle content wrapped in $ delimiters (for rows)
          if (currentKey === 'rows') {
            const rowContent = line.slice(1, -1); // Remove $ delimiters
            const cells = parseRowCells(rowContent);
            rowsContent.push(cells);
          }
        } else if (!line.startsWith('[') && !line.endsWith(']')) {
          // Single array item (for headers/alignment)
          if (currentKey === 'headers' || currentKey === 'alignment') {
            let item = line.replace(/^[\s"']+|[\s"',]+$/g, ''); // Remove quotes, commas, extra whitespace
            arrayContent.push(item);
          }
        }
      }
    }
    
    // Validate required fields
    if (!config.headers || !config.rows) {
      return null;
    }
    
    return config as TableConfig;
  } catch (error) {
    console.error('Error parsing table config:', error, configText);
    return null;
  }
};

export const parseTableBlocks = (content: string): ParsedTable[] => {
  const tables: ParsedTable[] = [];
  const tableRegex = /```table:data\s*\n([\s\S]*?)```/g;
  
  let match;
  let tableIndex = 0;
  
  while ((match = tableRegex.exec(content)) !== null) {
    const configText = match[1];
    const config = parseTableConfig(configText);
    
    if (config) {
      const placeholder = `TABLE_${tableIndex}`;
      tables.push({ placeholder, config });
      tableIndex++;
    }
  }
  
  return tables;
};

export const replaceTablesWithPlaceholders = (content: string, tables: ParsedTable[]): string => {
  let processedContent = content;
  
  // Sort tables by their position in content (reverse order to maintain indices)
  const tableRegex = /```table:data\s*\n[\s\S]*?```/g;
  const matches = Array.from(content.matchAll(tableRegex));
  
  // Replace from end to beginning to maintain string positions
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const table = tables[i];
    
    if (table && match.index !== undefined) {
      processedContent = 
        processedContent.substring(0, match.index) + 
        table.placeholder + 
        processedContent.substring(match.index + match[0].length);
    }
  }
  
  return processedContent;
};
